import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setFilterParams } from '../store/actions/filters';
import { filterSelector } from '../store/selectors/filters';
import { selectedAddressLocationSelector } from '../store/selectors/general';

export const useSyncHazardAssessmentParams = areaData => {
  const dispatch = useDispatch();
  const filterState = useSelector(filterSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const addressInfo = useSelector(selectedAddressLocationSelector);

  useEffect(() => {
    if (addressInfo) {
      if (addressInfo.county && areaData) {
        const countyInfo = areaData.counties.find(c => c.name === addressInfo.county);
        if (countyInfo) {
          dispatch(
            setFilterParams({
              selectedCounty: countyInfo.id,
              countySelectedFromAddress: true,
            }),
          );
        }
      }
    }
  }, [addressInfo, areaData, dispatch]);

  // Use refs to track whether each sync has occurred
  const initialSyncDone = useRef(false);
  const isReduxUpdating = useRef(false);
  const isUrlUpdating = useRef(false);

  // Sets redux based on URL search params. Set county and harris as defaults when search params don't exist
  useEffect(() => {
    if (!initialSyncDone.current) {
      const selectedCounty = searchParams.get('selectedCounty') || '';

      if (selectedCounty) {
        // Set redux updating to avoid infinite loop in url <--> redux updating
        isReduxUpdating.current = true;
        // Set filter in redux
        dispatch(
          setFilterParams({
            selectedCounty,
            countySelectedFromAddress: filterState.countySelectedFromAddress,
          }),
        );
        isReduxUpdating.current = false;
      }
      initialSyncDone.current = true;
    }
    // eslint-disable-next-line
  }, [searchParams, areaData]);

  // Update bounds when areaData becomes available
  useEffect(() => {
    if (areaData && filterState.selectedCounty) {
      const areaInfo = filterState.selectedCounty
        ? areaData.counties.find(d => d.id === filterState.selectedCounty)
        : null;
      if (areaInfo) {
        isReduxUpdating.current = true;
        dispatch(
          setFilterParams({
            ...filterState,
            bounds: areaInfo.bounds,
            areaName: areaInfo.name,
            countySelectedFromAddress: filterState.countySelectedFromAddress,
          }),
        );
        isReduxUpdating.current = false;
      }
    }
    // eslint-disable-next-line
  }, [areaData, filterState.selectedCounty]);

  // Sets the URL search params based on redux filters
  useEffect(() => {
    // Check to make sure that redux or the url aren't currently already updating
    if (isReduxUpdating.current || isUrlUpdating.current) return;

    const params = new URLSearchParams();

    // Sets each part of the url search params if they exist in redux
    if (filterState.selectedCounty) params.set('selectedCounty', filterState.selectedCounty);

    // Compare current search string with what we're about to set as the search string
    const paramsString = params.toString();
    const currentParamsString = searchParams.toString();
    if (paramsString !== currentParamsString) {
      isUrlUpdating.current = true;
      setSearchParams(params);
      isUrlUpdating.current = false;
    }
    // eslint-disable-next-line
  }, [filterState, searchParams]);

  return { filterState };
};
